import { AsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import { RequestStatus } from '../config/store';

export enum RequestType {
  Delete = 'delete',
  Get = 'get',
  Post = 'post',
  Put = 'put',
}

export type DefaultApiRequestState<DataState = any, T extends ReadonlyArray<string> = [RequestType.Get]> = {
  request: {
    [K in T extends ReadonlyArray<infer U> ? U : never]: {
      status: RequestStatus;
      error: string | null;
    };
  };
  data: DataState | null;
};

const getDefaultApiRequestState = (requests: RequestType[] = [RequestType.Get], data?: any) => {
  const result: any = {};
  requests.forEach((key) => {
    result[key] = {
      status: RequestStatus.Unsent,
      error: null,
    };
  });
  return {
    data: data || null,
    request: result,
  };
};

const getDefaultReducerForAsyncRequest = ({
  thunk,
  getInitialState = (state) => state,
  requestType = RequestType.Get,
  onSuccess = (state, payload) => {
    state.data = payload;
  },
  onError,
}: {
  thunk: AsyncThunk<any, any, any>;
  getInitialState?: (state: any) => any;
  requestType?: RequestType;
  onSuccess?: (state: any, payload: any) => void;
  onError?: (state: any, payload: any) => void;
}) => ({
  [thunk.pending.type]: (state: any) => {
    const stateChange = getInitialState(state);
    stateChange.request[requestType].status = RequestStatus.Pending;
  },
  [thunk.fulfilled.type]: (state: any, { payload }: PayloadAction<any>) => {
    const stateChange = getInitialState(state);
    stateChange.request[requestType].status = RequestStatus.Success;
    onSuccess(stateChange, payload);
  },
  [thunk.rejected.type]: (state: any, { error }: any) => {
    const stateChange = getInitialState(state);
    stateChange.request[requestType].status = RequestStatus.Failure;
    stateChange.request[requestType].error = error;
    if (onError) {
      onError(stateChange, error);
    }
  },
});

export { getDefaultApiRequestState, getDefaultReducerForAsyncRequest };
